import React, {
  useEffect,
  useRef,
  useState
} from 'react'
import autoComplete from '@tarekraafat/autocomplete.js'
import '@tarekraafat/autocomplete.js/dist/css/autoComplete.css'
import './autocomplete.scss'
import PropTypes from 'prop-types'

const Autocomplete = ({
  data,
  onSelect,
  onChange,
  onClear,
  name,
  id,
  value
}) => {
  const autoCompleteRef = useRef(null)
  const onSelectRef = useRef(onSelect)
  const onChangeRef = useRef(onChange)
  const valueRef = useRef(value)
  const [isInstanceReady, setIsInstanceReady] = useState(false)

  onSelectRef.current = onSelect
  onChangeRef.current = onChange
  valueRef.current = value

  useEffect(() => {
    if (data.length === 0 || id?.length === 0) {
      return undefined
    }

    const timeoutId = setTimeout(() => {
      if (autoCompleteRef.current) {
        return
      }

      // eslint-disable-next-line new-cap
      autoCompleteRef.current = new autoComplete({
        selector: `#${id}`,
        placeHolder: '',
        data: {
          src: data || [],
          keys: ['name']
        },
        resultItem: {
          highlight: true
        },
        events: {
          input: {
            selection: (event) => {
              const selectedValue =
                event.detail.selection.value

              autoCompleteRef.current.input.value =
                selectedValue.name

              onSelectRef.current(selectedValue)

              if (onChangeRef.current) {
                onChangeRef.current(selectedValue.name || '')
              }
            }
          }
        }
      })

      const currentValue = valueRef.current
      autoCompleteRef.current.input.value =
        typeof currentValue === 'string'
          ? currentValue
          : currentValue?.name || ''
      setIsInstanceReady(true)
    }, 0)

    return () => {
      clearTimeout(timeoutId)
      if (autoCompleteRef.current) {
        autoCompleteRef.current = null
      }
    }
  }, [data, id])
  useEffect(() => {
    if (!autoCompleteRef.current) {
      return
    }
    const input =
      autoCompleteRef.current.input

    if (!input) {
      return
    }

    if (!value) {
      input.value = ''
      return
    }

    if (typeof value === 'object') {
      input.value = value.name || ''
      return
    }

    input.value = value
  }, [value])
  useEffect(() => {
    if (!autoCompleteRef.current) {
      return undefined
    }
    const input =
      autoCompleteRef.current.input
    if (!input) {
      return undefined
    }
    const handleInput = (event) => {
      const inputValue =
        event.target.value

      if (onChange) {
        onChange(inputValue)
      }

      if (
        inputValue === '' &&
        onClear
      ) {
        onClear()
      }
    }

    input.addEventListener(
      'input',
      handleInput
    )
    return () => {
      input.removeEventListener(
        'input',
        handleInput
      )
    }
  }, [
    onChange,
    onClear,
    isInstanceReady
  ])

  return (
    <input
      id={`${id}`}
      className='form-control'
      name={name}
      type='search'
      dir='ltr'
      spellCheck={false}
      autoCorrect='off'
      autoComplete='off'
      autoCapitalize='off'
    />
  )
}

Autocomplete.propTypes = {
  data: PropTypes.array.isRequired,
  onSelect: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  name: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object
  ]),
  id: PropTypes.string
}

Autocomplete.defaultProps = {
  onChange: null,
  onClear: null,
  name: '',
  value: '',
  id: ''
}

export default Autocomplete
